import type { ReactNode } from 'react';
import { Modal } from './Modal.js';

/**
 * A mesa: um registro aberto por cima da lista, para quem opera preencher o
 * que a fase pede, mandar o registro para a fase certa e passar ao próximo sem
 * perder a lista. Nasceu no sinistro (29/09/2026, a quarta exceção ao "modal
 * como primeira ideia" do `DESIGN.md`) e virou da casa em 02/10, a pedido do
 * dono, para os outros módulos que levam um registro de fase em fase.
 *
 * ## A anatomia
 *
 * - **cabeçalho**: título, descrição e, ao lado, o `summary` — o
 *   `SummaryStrip` com o que quem opera consulta a cada campo;
 * - **trilha** (`trail`): a `StepTrail`, por onde o registro passou;
 * - **três colunas**: à esquerda (`left`) os registros ligados
 *   (`RelatedGroup`) ou um `Checklist`; no miolo (`children`) as abas; à
 *   direita (`right`) o `TransitionPanel`;
 * - **gaveta** (`drawer`): a `SideDrawer`, que cobre a coluna da direita e
 *   avança um pouco sobre o miolo, sem véu;
 * - **rodapé** (`footer`, 28/10/2026): os atos lado a lado, alinhados à
 *   direita — para a mesa cujos destinos são poucos e não pedem uma coluna
 *   inteira. Sem `right`, a mesa fica com duas colunas (a da despesa no
 *   Financeiro, que tem dois atos).
 *
 * A altura desconta o cabeçalho, que é mais alto que o de um diálogo comum
 * porque carrega o `SummaryStrip` — com o desconto de antes (9rem), o botão de
 * mover ficava cortado no rodapé numa janela de 950px.
 *
 * Quem decide o que vai em cada lugar é o módulo — a mesa não sabe o que é um
 * sinistro, uma demanda ou uma cobrança.
 *
 * ## O Esc é da gaveta primeiro
 *
 * Com a gaveta aberta, o primeiro Esc a fecha (`onCloseDrawer`) e só o segundo
 * fecha a mesa. Sem isso, quem só queria guardar a foto perdia o registro.
 *
 * ## No telefone
 *
 * As três colunas empilham e rolam juntas; a gaveta vira folha de baixo.
 */
export function DeskModal({
  open,
  onOpenChange,
  title,
  descricao,
  summary,
  titleAddon,
  belowTitle,
  trail,
  left,
  leftLabel,
  children,
  middleLabel,
  right,
  footer,
  drawer,
  onCloseDrawer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  descricao?: ReactNode;
  summary?: ReactNode;
  /** Ao lado do título — o responsável. Ver `Modal`. */
  titleAddon?: ReactNode;
  /** Abaixo da descrição — as etiquetas. Ver `Modal`. */
  belowTitle?: ReactNode;
  trail?: ReactNode;
  left: ReactNode;
  /** "Envolvidos no sinistro". */
  leftLabel: string;
  /** O miolo — as abas Formulário, Atividade, Anexos. */
  children: ReactNode;
  /** "Conteúdo do sinistro". */
  middleLabel: string;
  /** O `TransitionPanel`. Ausente, a mesa tem duas colunas. */
  right?: ReactNode;
  /** Os atos no rodapé, alinhados à direita. Ausente (ou vazio), não há rodapé. */
  footer?: ReactNode;
  /** A `SideDrawer` aberta, ou nada. */
  drawer?: ReactNode;
  /** Obrigatória quando há `drawer`: é o que o primeiro Esc chama. */
  onCloseDrawer?: () => void;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="2xl"
      title={title}
      descricao={descricao}
      aside={summary}
      titleAddon={titleAddon}
      belowTitle={belowTitle}
      onEscapeKeyDown={(event) => {
        if (drawer && onCloseDrawer) {
          event.preventDefault();
          onCloseDrawer();
        }
      }}
    >
      <div className="flex h-[min(52rem,calc(100dvh-12rem))] min-h-0 flex-col">
        {trail}
        {/*
         * `clip`, e não `hidden`, no desktop: `hidden` corta mas ainda ROLA por
         * programa — o foco num campo e o `scrollIntoView` da mesa rolavam a
         * grade, as três colunas subiam por baixo da trilha e sobrava uma faixa
         * vazia embaixo, sem barra para desfazer. `clip` corta e não rola; quem
         * rola são as colunas.
         */}
        <div
          className={
            right
              ? 'relative grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[17rem_minmax(0,1fr)_18rem] lg:overflow-clip'
              : 'relative grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[17rem_minmax(0,1fr)] lg:overflow-clip'
          }
        >
          <aside
            aria-label={leftLabel}
            className="flex min-w-0 flex-col gap-1 border-b border-borda-suave px-3 py-4 lg:overflow-y-auto lg:border-r lg:border-b-0"
          >
            {left}
          </aside>
          <section
            aria-label={middleLabel}
            className="min-w-0 px-4 py-4 lg:overflow-y-auto"
          >
            {children}
          </section>
          {right}
          {drawer}
        </div>
        {footer && (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-borda px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </Modal>
  );
}
